// src/App.js
import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Cmdbeginner from "./component/Cmdbeginner";
import { AnimatePresence, motion } from "framer-motion";
import MotionWrapper from "./component/MotionWrapper";
// App.css removed, using global Tailwind styles

function App() {
  // eslint-disable-next-line
  const [cmdbeginnerKey, setCmdbeginnerKey] = useState(Date.now());
  const [currentComponent, setCurrentComponent] = useState(null);
  const [currentKey, setCurrentKey] = useState("home");
  const [showModal, setShowModal] = useState(true);
  const [playerKey, setPlayerKey] = useState("");

  // Clear remote key on init
  useEffect(() => {
    sessionStorage.removeItem("playerKey");
    setPlayerKey("");
  }, []);

  // Update component key when modal closes
  useEffect(() => {
    if (!showModal) {
      const newKey = Date.now();
      setCmdbeginnerKey(newKey);
      setCurrentComponent(<Cmdbeginner key={newKey} />);
      setCurrentKey("cmdbeginner");
    }
  }, [showModal]);

  const handleInputChange = (event) => {
    setPlayerKey(event.target.value);
  };

  const handleSave = () => {
    sessionStorage.setItem("playerKey", playerKey);
    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSave();
    }
  };

  const changeComponent = (component, key) => {
    setCurrentComponent(component);
    setCurrentKey(key || Date.now());
  };

  return (
    <div className="flex min-h-screen bg-hsr-dark overflow-hidden text-gray-200">

      {/* Sidebar - Persistent */}
      {!showModal && (
        <Sidebar changeComponent={changeComponent} />
      )}

      {/* Main Content Area */}
      {!showModal && (
        <main className="flex-1 relative h-screen overflow-y-auto p-4 lg:p-8 scroll-smooth custom-scrollbar">
          {/* Global Header */}
          <div className="w-full max-w-7xl mx-auto pt-12 lg:pt-0">
            <Header />
            <AnimatePresence mode="wait">
              {currentComponent && (
                <MotionWrapper key={currentKey} className="w-full">
                  {currentComponent}
                </MotionWrapper>
              )}
            </AnimatePresence>
          </div>
        </main>
      )}

      {/* Glassmorphic Modal Overlay */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, type: "spring", bounce: 0.3 }}
              className="glass-panel w-full max-w-2xl p-0 rounded-xl overflow-hidden"
            >

              {/* Modal Header */}
              <div className="px-8 py-6 border-b border-white/10 bg-white/5">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-astral-purple-glow to-astral-purple bg-clip-text text-transparent">
                  HotaruSR - Remote Server Key
                </h2>
              </div>

              {/* Modal Body */}
              <div className="p-8 space-y-6">
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <p className="text-red-400 font-bold text-sm mb-1">* Notice</p>
                  <p className="text-gray-300 text-sm">
                    This feature is only supported in Version <span className="font-bold text-white">3.1.5 or upper</span>.
                    It allows players to execute commands directly from the website to their in-game account.
                  </p>
                </div>

                <p className="text-gray-400 text-sm leading-relaxed">
                  Please use the command <code className="bg-black/30 px-2 py-0.5 rounded text-stardust-gold font-mono">/remote</code> in-game to get your player key.
                  This key is only saved in your game session and will be removed after you log out or turn off your game!
                </p>

                <div className="rounded-lg overflow-hidden border border-white/10 shadow-lg">
                  <img
                    src="https://raw.githubusercontent.com/arimetson/perfect-relics-lcweb/refs/heads/main/remoteCommandImage.png"
                    alt="Welcome"
                    className="w-full h-auto object-cover opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-astral-purple-glow font-medium text-sm">Enter your player key below!</label>
                  <input
                    type="text"
                    placeholder="Enter your player key..."
                    value={playerKey}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    className="glass-input text-lg py-3"
                    autoFocus
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-8 py-6 bg-black/20 flex justify-end gap-4 border-t border-white/5">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2.5 rounded-lg text-red-400 hover:text-white hover:bg-red-500/20 border border-transparent hover:border-red-500/30 transition-all font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-8 py-2.5 rounded-lg bg-gradient-to-r from-astral-purple to-indigo-600 text-white font-bold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Enter System
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
